import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import MonthDayPicker from '../../components/common/MonthDayPicker/MonthDayPicker';
import CountrySelectWithFlag from '../../components/common/SelectBox/CountrySelectWithFlag';
import ErrorNotification from '../../components/common/AlertBox/ErrorNotification';
import { SuccessAlert } from '../../components/common/AlertBox/GreenAlert';
import { fetchActiveCountries } from '../../services/Localization/CountryService';
import { createFinancialYear } from '../../services/Localization/FinancialYearService';
import '../../components/common/CommonText.css';
import './FinancialYear.css';

const FinancialYearAdd = () => {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countries = await fetchActiveCountries();
        setCountryOptions(countries || []);
      } catch (err) {
        console.error("Error loading countries:", err);
      }
    };
    loadCountries();
  }, []);

  const handleCountrySelect = (e) => {
    const countryId = Number(e.target.value);
    if (!countryId) return;
    const selectedObj = countryOptions.find(c => Number(c.value) === countryId);
    if (selectedObj && !selectedCountries.some(c => Number(c.value) === countryId)) {
      setSelectedCountries(prev => [...prev, selectedObj]);
    }
  };

  const removeCountry = (countryId) => {
    setSelectedCountries(prev => prev.filter(c => Number(c.value) !== Number(countryId)));
  };

  const formatDateString = (dateObj) => {
    if (!dateObj) return "";
    return dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setErrors({});

    if (!startDate) {
      setErrors({ startDate: "Start Date is required" });
      return;
    }
    if (!endDate) {
      setErrors({ endDate: "End Date is required" });
      return;
    }

    const startMonth = startDate.getMonth() + 1;
    const startDay = startDate.getDate();
    const endMonth = endDate.getMonth() + 1;
    const endDay = endDate.getDate();
    const startStr = formatDateString(startDate);
    const endStr = formatDateString(endDate);

    const payload = {
      name: `${startStr} - ${endStr}`,
      start_month: startMonth,
      start_day: startDay,
      end_month: endMonth,
      end_day: endDay,
      country_ids: selectedCountries.map(c => Number(c.value))
    };

    setLoading(true);
    try {
      const res = await createFinancialYear(payload);
      if (res && res.response_code === 1) {
        SuccessAlert("Financial Year created successfully");
        navigate('/financial-year');
      } else {
        if (typeof res?.message === 'string') {
          setErrors({ general: res.message });
        } else if (res?.message && typeof res.message === 'object') {
          setErrors(res.message);
        } else {
          setErrors({ general: "Failed to create financial year" });
        }
      }
    } catch (err) {
      console.error(err);
      setErrors({ general: "An error occurred while saving." });
    } finally {
      setLoading(false);
    }
  };

  const availableCountryOptions = countryOptions.filter(
    (c) => !selectedCountries.some((sc) => Number(sc.value) === Number(c.value))
  );

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader
          pageName="Localization /"
          nestedpageName="Add Financial Year"
          linkTo="/financial-year"
        />
      </div>

      <div className="fy-container">
        <ErrorNotification errors={errors} marginBottom="16px" />

        <div className="fy-card">
          <div className="fy-card-header">
            <h2 className="fy-card-title">Add New Financial Year</h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="fy-form-group">
              <MonthDayPicker
                label="Start Date"
                selectedDate={startDate}
                onChange={(date) => setStartDate(date)}
                isRequired
                error={errors.startDate}
              />
            </div>

            <div className="fy-form-group">
              <MonthDayPicker
                label="End Date"
                selectedDate={endDate}
                onChange={(date) => setEndDate(date)}
                isRequired
                error={errors.endDate}
              />
            </div>

            <div className="fy-section-title">Select Country</div>

            <div className="fy-form-group">
              <CountrySelectWithFlag
                label="Country"
                options={availableCountryOptions}
                onChange={handleCountrySelect}
                placeholder="Select Country"
              />
            </div>

            {selectedCountries.length > 0 && (
              <div className="fy-country-pills-container">
                {selectedCountries.map((country) => (
                  <div key={country.value} className="fy-country-pill">
                    <span>{country.label}</span>
                    <span
                      className="remove-icon"
                      onClick={() => removeCountry(country.value)}
                    >
                      ✕
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="fy-actions">
              <button
                type="submit"
                className="fy-submit-btn"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Financial Year"}
              </button>
              <button
                type="button"
                className="fy-cancel-btn"
                onClick={() => navigate('/financial-year')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FinancialYearAdd;
